// src/pages/AdminAnalytics.js
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { ref, onValue } from "firebase/database";

// Prefer short room number like "A212"
function roomLabel(roomObj, roomId) {
  if (!roomObj) return roomId || "-";
  return roomObj.n || roomId || "-";
}

export default function AdminAnalytics() {
  const [slots, setSlots] = useState([]);
  const [slotId, setSlotId] = useState("");
  const [activeTab, setActiveTab] = useState("checked"); // checked | notchecked

  // Attendance
  const [attendanceBySlot, setAttendanceBySlot] = useState({});
  const [attendanceAll, setAttendanceAll] = useState({});
  const [attendanceSlotTop, setAttendanceSlotTop] = useState({});

  // Entities
  const [members, setMembers] = useState({});
  const [membersByRoll, setMembersByRoll] = useState({});
  const [teams, setTeams] = useState({});
  const [rooms, setRooms] = useState({});
  const [vols, setVols] = useState({});
  const [auditIndex, setAuditIndex] = useState({});

  // UI
  const [q, setQ] = useState("");

  // load master data
  useEffect(() => {
    const offSlots = onValue(ref(db, "slots"), (snap) => {
      const v = snap.val() || {};
      const arr = Object.entries(v).map(([id, s]) => ({ id, ...s }));
      arr.sort((a, b) => (a.st || 0) - (b.st || 0));
      setSlots(arr);
      if (!slotId && arr.length) setSlotId(arr[arr.length - 1].id); // latest by default
    });

    const offMembers = onValue(ref(db, "members"), (snap) => setMembers(snap.val() || {}));
    const offMembers2 = onValue(ref(db, "membersByRoll"), (snap) => setMembersByRoll(snap.val() || {}));
    const offTeams = onValue(ref(db, "teams"), (snap) => setTeams(snap.val() || {}));
    const offRooms = onValue(ref(db, "rooms"), (snap) => setRooms(snap.val() || {}));
    const offVols = onValue(ref(db, "volunteers"), (snap) => setVols(snap.val() || {}));

    const offAttAll = onValue(ref(db, "attendance"), (snap) => {
      const val = snap.val() || {};
      setAttendanceAll(val);
      setAttendanceSlotTop(val); // in case you ever store /attendance/{slot}/{roll}
    });

    const offAudit = onValue(ref(db, "audit/checkins"), (snap) => {
      const v = snap.val() || {};
      const idx = {};
      Object.values(v).forEach((e) => {
        const s = e.slot, r = e.roll;
        if (!s || !r) return;
        if (!idx[s]) idx[s] = {};
        const cur = idx[s][r];
        if (!cur || (e.at || 0) > (cur.at || 0)) {
          idx[s][r] = { by: e.by, byEmail: e.byEmail || "", byName: e.byName || "", at: e.at || 0 };
        }
      });
      setAuditIndex(idx);
    });

    return () => { offSlots(); offMembers(); offMembers2(); offTeams(); offRooms(); offVols(); offAttAll(); offAudit(); };
  }, [slotId]);

  // per slot view
  useEffect(() => {
    if (!slotId) { setAttendanceBySlot({}); return; }
    const off = onValue(ref(db, `attendanceBySlot/${slotId}`), (snap) => {
      setAttendanceBySlot(snap.val() || {});
    });
    return () => off();
  }, [slotId]);

  // unified member map (lowercase rolls)
  const memberMap = useMemo(() => {
    const out = {};
    Object.entries(members || {}).forEach(([roll, v]) => {
      const k = roll.toLowerCase();
      out[k] = {
        roll: k,
        name: v.n || v.name || k.toUpperCase(),
        email: v.e || v.email || "",
        branch: v.b || v.branch || "",
        section: v.s || v.section || "",
        teamId: v.t || v.teamId || "",
        mobile: v.m || "",
        noc: !!v.noc,
      };
    });
    Object.entries(membersByRoll || {}).forEach(([roll, v]) => {
      const k = roll.toLowerCase();
      out[k] = {
        ...(out[k] || {}),
        roll: k,
        name: v.name || out[k]?.name || k.toUpperCase(),
        email: v.email || out[k]?.email || "",
        branch: v.branch || out[k]?.branch || "",
        section: v.section || out[k]?.section || "",
        teamId: v.teamId || out[k]?.teamId || out[k]?.t || "",
      };
    });
    return out;
  }, [members, membersByRoll]);

  // checked-in rows (from any shape)
  const rows = useMemo(() => {
    // priority: attendanceBySlot
    let source = attendanceBySlot;
    let have = Object.keys(source || {}).length;

    // fallback: /attendance/{roll}/{slot}
    if (!have) {
      const fromAll = {};
      Object.entries(attendanceAll || {}).forEach(([roll, perRoll]) => {
        if (perRoll && perRoll[slotId]) {
          const v = perRoll[slotId];
          fromAll[roll.toLowerCase()] = { at: v.at || v.t || 0, byVol: v.by || v.byVol || v.v || "" };
        }
      });
      source = fromAll;
      have = Object.keys(source).length;
    }

    // optional fallback: /attendance/{slot}/{roll}
    if (!have) {
      const slotNode = (attendanceSlotTop || {})[slotId] || {};
      const fromSlot = {};
      Object.entries(slotNode).forEach(([roll, v]) => {
        if (v && typeof v === "object") {
          fromSlot[roll.toLowerCase()] = { at: v.at || v.t || 0, byVol: v.by || v.byVol || v.v || "" };
        }
      });
      source = fromSlot;
    }

    const result = [];
    Object.entries(source || {}).forEach(([rollLc, v]) => {
      const roll = rollLc.toLowerCase();
      const m = memberMap[roll] || {};
      // resolve team
      let teamId = m.teamId || "";
      if (!teamId) {
        Object.entries(teams || {}).some(([tid, t]) => {
          if (t?.m && t.m[roll]) { teamId = tid; return true; }
          return false;
        });
      }
      const tObj = (teams || {})[teamId] || {};
      const teamName = tObj.n || teamId || "";

      // room/table
      const roomId = tObj.room || "";
      const table = tObj.tb || "";
      const rLabel = roomLabel((rooms || {})[roomId], roomId);

      // volunteer pretty
      const raw = v.byVol || v.by || "";
      let volLabel = "-";
      if (vols[raw]) volLabel = vols[raw].name || vols[raw].email || raw;
      else if ((auditIndex[slotId] || {})[roll]) {
        const ai = (auditIndex[slotId] || {})[roll];
        volLabel = ai.byName || ai.byEmail || raw || "-";
      } else {
        volLabel = raw || "-";
      }

      result.push({
        roll,
        name: m.n || roll.toUpperCase(),
        email: m.email || "",
        teamName,
        roomLabel: rLabel,
        table,
        at: v.at || v.t || 0,
        byVol: volLabel,
        mobile: m.mobile || "",
        noc: !!m.noc
      });
    });
    // newest first
    result.sort((a, b) => (b.at || 0) - (a.at || 0));
    return result;
  }, [attendanceBySlot, attendanceAll, attendanceSlotTop, memberMap, teams, rooms, vols, auditIndex, slotId]);

  // absent = all members - present in this slot
  const presentSet = useMemo(() => new Set(rows.map(r => r.roll)), [rows]);
  const absent = useMemo(() =>
    Object.values(memberMap).filter(m => !presentSet.has(m.roll)),
  [memberMap, presentSet]);

  // KPIs
  const totalTeams = useMemo(() => Object.keys(teams || {}).length, [teams]);
  const totalParticipants = useMemo(() => Object.keys(memberMap).length, [memberMap]);
  const totalCheckedIn = rows.length;
  const totalPending = absent.length;
  const totalNoc = useMemo(() => Object.values(memberMap).filter(m => m.noc).length, [memberMap]);

  const currentSlot = slots.find(s => s.id === slotId);

  const tableData = activeTab === "checked"
  ? rows
  : absent.map(m => ({
      roll: m.roll,
      name: m.name,
      email: m.email,
      mobile: m.mobile || "-",
      teamName: (() => {
        let teamId = m.teamId || "";
        if (!teamId) {
          Object.entries(teams || {}).some(([tid, t]) => {
            if (t?.m && t.m[m.roll]) { teamId = tid; return true; }
            return false;
          });
        }
        return (teams[teamId]?.n || teamId || "-");
      })(),
      roomLabel: (() => {
        let teamId = m.teamId || "";
        if (!teamId) {
          Object.entries(teams || {}).some(([tid, t]) => {
            if (t?.m && t.m[m.roll]) { teamId = tid; return true; }
            return false;
          });
        }
        const tObj = teams[teamId] || {};
        const roomId = tObj.room || "";
        return roomLabel(rooms[roomId], roomId);
      })(),
      table: (() => {
        let teamId = m.teamId || "";
        if (!teamId) {
          Object.entries(teams || {}).some(([tid, t]) => {
            if (t?.m && t.m[m.roll]) { teamId = tid; return true; }
            return false;
          });
        }
        return teams[teamId]?.tb || "";
      })(),
      byVol: "-"
    }));


  // const tableData = activeTab === "checked"
  //   ? rows
  //   : absent.map(m => ({
  //       roll: m.roll, name: m.name, email: m.email,
  //       teamName: (() => {
  //         let teamId = m.teamId || "";
  //         if (!teamId) {
  //           Object.entries(teams || {}).some(([tid, t]) => {
  //             if (t?.m && t.m[m.roll]) { teamId = tid; return true; }
  //             return false;
  //           });
  //         }
  //         return (teams[teamId]?.n || teamId || "-");
  //       })(),
  //       roomLabel: (() => {
  //         let teamId = m.teamId || "";
  //         if (!teamId) {
  //           Object.entries(teams || {}).some(([tid, t]) => {
  //             if (t?.m && t.m[m.roll]) { teamId = tid; return true; }
  //             return false;
  //           });
  //         }
  //         const tObj = teams[teamId] || {};
  //         const roomId = tObj.room || "";
  //         return roomLabel(rooms[roomId], roomId);
  //       })(),
  //       table: (() => {
  //         let teamId = m.teamId || "";
  //         if (!teamId) {
  //           Object.entries(teams || {}).some(([tid, t]) => {
  //             if (t?.m && t.m[m.roll]) { teamId = tid; return true; }
  //             return false;
  //           });
  //         }
  //         return teams[teamId]?.tb || "";
  //       })(),
  //       byVol: "-"
  //     }));

  // search filter
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return tableData;
    return tableData.filter(r =>
      (r.roll || "").toLowerCase().includes(term) ||
      (r.name || "").toLowerCase().includes(term) ||
      (r.email || "").toLowerCase().includes(term) ||
      (r.teamName || "").toLowerCase().includes(term) ||
      (r.roomLabel || "").toLowerCase().includes(term) ||
      (r.table ? String(r.table).toLowerCase().includes(term) : false) ||
      (r.byVol || "").toLowerCase().includes(term)
    );
  }, [tableData, q]);

  const StatCard = ({ label, value, color }) => (
    <div className={`flex flex-col items-center justify-center rounded-2xl p-6 shadow text-white font-semibold ${color || "bg-gray-600"}`}>
      <div className="text-3xl">{value}</div>
      <div className="text-sm mt-1 opacity-80">{label}</div>
    </div>
  );

  return (
    <div className="space-y-6">
      <h1 className="text-2xl md:text-3xl font-bold">Admin — Analytics</h1>

      {/* Slot & tabs */}
      <div className="flex flex-wrap gap-3 items-center">
        <select className="p-3 border rounded-xl" value={slotId} onChange={e=>setSlotId(e.target.value)}>
          {slots.map(s => (
            <option key={s.id} value={s.id}>
              {s.n || s.id} ({s.st ? new Date(s.st).toLocaleString() : "?"} – {s.et ? new Date(s.et).toLocaleString() : "?"})
            </option>
          ))}
        </select>

        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab("checked")}
            className={`px-4 py-2 rounded-xl border ${activeTab === "checked" ? "bg-black text-white" : "bg-white hover:bg-gray-50"}`}
          >
            ✅ Checked-In ({totalCheckedIn})
          </button>
          <button
            onClick={() => setActiveTab("notchecked")}
            className={`px-4 py-2 rounded-xl border ${activeTab === "notchecked" ? "bg-black text-white" : "bg-white hover:bg-gray-50"}`}
          >
            ❌ Not Checked-In ({totalPending})
          </button>
        </div>

        <input
          className="p-3 border rounded-xl flex-1 min-w-[220px]"
          placeholder="Search roll / name / email / team / room / volunteer / table"
          value={q}
          onChange={(e)=>setQ(e.target.value)}
        />
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard label="Total Teams" value={totalTeams} color="bg-indigo-600" />
        <StatCard label="Total Participants" value={totalParticipants} color="bg-blue-600" />
        <StatCard label="Total NOC Submitted" value={totalNoc} color="bg-green-600" />
        <StatCard
          label="Check-in Pending"
          value={totalPending}
          color={totalPending === 0 ? "bg-green-600" : "bg-red-600"}
        />
        <StatCard label="Checked-In" value={totalCheckedIn} color="bg-emerald-600" />
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow p-6 overflow-x-auto">
        <div className="text-sm text-gray-700 mb-3">
          Slot: <b>{currentSlot?.n || currentSlot?.id || "-"}</b>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left">
              {activeTab === "checked" && <th className="py-2 pr-4">Time</th>}
              <th className="py-2 pr-4">Roll</th>
              <th className="py-2 pr-4">Name</th>
              <th className="py-2 pr-4">Team</th>
                            <th className="py-2 pr-4">Mobile</th>
              <th className="py-2 pr-4">Room / Table</th>
              {activeTab === "checked" && <th className="py-2 pr-4">Volunteer</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={`${r.roll}-${r.at || "abs"}`} className="border-b">
                {activeTab === "checked" && (
                  <td className="py-2 pr-4">{r.at ? new Date(r.at).toLocaleString() : "-"}</td>
                )}
                <td className="py-2 pr-4 font-mono">{r.roll}</td>
                <td className="py-2 pr-4">{r.name}</td>
                <td className="py-2 pr-4">{r.teamName || "-"}</td>
                 <td className="py-2 pr-4">{r.mobile || "-"}</td>

                <td className="py-2 pr-4">{r.roomLabel || "-"}{r.table ? ` / ${r.table}` : ""}</td>
                {activeTab === "checked" && <td className="py-2 pr-4">{r.byVol || "-"}</td>}
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={activeTab === "checked" ? 6 : 4} className="py-4 text-gray-500 text-center">
                  No records in this view.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
